import type { PlatformAdapter } from "./PlatformAdapter";
import type { HapticStyle, PlayerProfile, SaveData } from "../core/types";
import { DEFAULT_SAVE } from "../core/types";
import { ADSGRAM_BLOCK_ID } from "../config";

// Minimal subset of the Telegram WebApp API we use.
interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  initData: string;
  platform: string;
  initDataUnsafe: {
    user?: { id: number; username?: string; first_name?: string; is_premium?: boolean };
  };
  themeParams: Record<string, string>;
  viewportHeight: number;
  viewportStableHeight: number;
  HapticFeedback?: {
    impactOccurred: (s: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred: (s: "error" | "success" | "warning") => void;
  };
  CloudStorage?: {
    setItem: (k: string, v: string, cb?: (err: unknown, ok: boolean) => void) => void;
    getItem: (k: string, cb: (err: unknown, value: string) => void) => void;
  };
  shareMessage?: (msgId: string) => void;
  openTelegramLink: (url: string) => void;
  setHeaderColor?: (c: string) => void;
  setBackgroundColor?: (c: string) => void;
}

// Adsgram rewarded-ad SDK (loaded via <script> in index.html).
interface AdsgramController {
  show: () => Promise<void>;
}
interface AdsgramSDK {
  init: (opts: { blockId: string }) => AdsgramController;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
    Adsgram?: AdsgramSDK;
  }
}

const SAVE_KEY = "td_save_v1";

/** Telegram Mini App adapter. Uses Telegram WebApp SDK + CloudStorage. */
export class TelegramAdapter implements PlatformAdapter {
  readonly name = "telegram";
  private tg!: TelegramWebApp;
  private adController: AdsgramController | null = null;

  static isAvailable(): boolean {
    if (typeof window === "undefined") return false;
    const tg = window.Telegram?.WebApp;
    if (!tg) return false;
    // The SDK script always installs a `WebApp` stub even in a regular browser.
    // Real Telegram clients always populate initData and report a non-"unknown" platform.
    if (tg.initData && tg.initData.length > 0) return true;
    if (tg.platform && tg.platform !== "unknown") return true;
    return false;
  }

  async init(): Promise<void> {
    const tg = window.Telegram?.WebApp;
    if (!tg) throw new Error("Telegram WebApp not available");
    this.tg = tg;
    tg.ready();
    tg.expand();
    tg.setHeaderColor?.("#0b1020");
    tg.setBackgroundColor?.("#0b1020");
  }

  getPlayer(): PlayerProfile {
    const u = this.tg.initDataUnsafe.user;
    if (!u) return { id: "tg_anonymous", username: "Player" };
    return {
      id: `tg_${u.id}`,
      username: u.username ?? u.first_name ?? "Player",
      isPremium: u.is_premium,
    };
  }

  async load(): Promise<SaveData> {
    const cs = this.tg.CloudStorage;
    if (!cs) return { ...DEFAULT_SAVE };
    return new Promise((resolve) => {
      cs.getItem(SAVE_KEY, (err, value) => {
        if (err || !value) return resolve({ ...DEFAULT_SAVE });
        try {
          resolve({ ...DEFAULT_SAVE, ...JSON.parse(value) });
        } catch {
          resolve({ ...DEFAULT_SAVE });
        }
      });
    });
  }

  async save(data: SaveData): Promise<void> {
    const cs = this.tg.CloudStorage;
    if (!cs) return;
    return new Promise((resolve) => {
      cs.setItem(SAVE_KEY, JSON.stringify(data), () => resolve());
    });
  }

  haptic(style: HapticStyle): void {
    const hf = this.tg.HapticFeedback;
    if (!hf) return;
    if (style === "success" || style === "warning" || style === "error") {
      hf.notificationOccurred(style);
    } else {
      hf.impactOccurred(style);
    }
  }

  async share(text: string): Promise<void> {
    // shareMessage requires server-prepared message id; fall back to deeplink share.
    const url = `https://t.me/share/url?url=${encodeURIComponent(text)}`;
    this.tg.openTelegramLink(url);
  }

  async showRewardedAd(): Promise<{ rewarded: boolean }> {
    // Not configured yet: grant the reward so the game is never blocked while
    // the owner is still setting up their Adsgram account. No money is earned.
    if (!ADSGRAM_BLOCK_ID) return { rewarded: true };
    const Adsgram = window.Adsgram;
    if (!Adsgram) return { rewarded: false };
    try {
      if (!this.adController) this.adController = Adsgram.init({ blockId: ADSGRAM_BLOCK_ID });
      // Resolves when the user watches the full ad; rejects if closed early / no fill.
      await this.adController.show();
      return { rewarded: true };
    } catch {
      return { rewarded: false };
    }
  }

  async purchase(productId: string): Promise<{ success: boolean }> {
    // TODO Phase 7: hit backend /invoice, get invoice link, then tg.openInvoice.
    console.log("[TelegramAdapter] purchase (Stars TODO)", productId);
    return { success: false };
  }

  getViewport() {
    return { width: window.innerWidth, height: this.tg.viewportStableHeight || window.innerHeight };
  }
}
